import { zodResolver } from "@hookform/resolvers/zod";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Modal } from "@/shared/ui/Modal";
import { Textarea } from "@/shared/ui/textarea";

import { ExpenseCalculator } from "./ExpenseCalculator";
import { Button } from "@/shared/ui/button";
import { useUpdateLink } from "@/entities/lead-link";

const schema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  costs: z.preprocess(
    (a) => {
      if (a === "" || a === null || a === undefined) return undefined;
      return Number(a);
    },
    z.number().min(0, "Сумма должна быть не меньше 0"),
  ),
});

type FormValues = z.infer<typeof schema>;

export const EditLinkModal = ({
  open,
  onClose,
  link,
}: {
  open: boolean;
  onClose: () => void;
  link: {
    id: string;
    name: string;
    description: string | null;
    costs: number;
  };
  disabled?: boolean;
}) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: link.name,
      description: link.description || "",
      costs: link.costs,
    },
  });

  const { mutateAsync, isPending } = useUpdateLink(link.id);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const updatedValues = Object.fromEntries(
      Object.entries(values).filter(
        ([key, value]) => value !== link[key as keyof typeof link],
      ),
    );

    try {
      await mutateAsync(updatedValues);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal open={open} onOpenChange={onClose}>
      <Modal.Content>
        <Modal.Title>Редактировать ссылку</Modal.Title>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 [&_label]:text-xs [&_label]:font-normal [&_label]:text-neutral-500"
        >
          <div>
            <Label htmlFor="name">Название ссылки</Label>
            <Input id="name" {...register("name")} />
            {errors.name && (
              <p className="text-xs text-red-500">
                Название ссылки должно содержать хотя бы 3 символа
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Описание ссылки</Label>
            <Textarea
              id="description"
              className="shadow-none"
              {...register("description")}
            />
          </div>

          <Controller
            name="costs"
            control={control}
            render={({ field }) => (
              <ExpenseCalculator
                amount={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <div className="flex flex-col gap-2">
            <Button type="submit" disabled={!isValid || isPending}>
              Обновить ссылку
            </Button>
            <Modal.Close />
          </div>
        </form>
      </Modal.Content>
    </Modal>
  );
};
